// maybe-prompt-ai-orchestrator-after-ai-setup.ts
//
// Sau AI setup phase, QUYẾT ĐỊNH có hỏi enable ai-orchestrator không — dựa trên
// kết quả setup key (provider + số model). Flow user yêu cầu:
//
//   - Subscription: LUÔN hỏi (Claude Pro/Max/Team có 3 model opus/sonnet/haiku
//     → đủ phân loại việc; picker đưa 3 alias để map).
//   - LLM/AI key (llmlite/anthropic):
//       • Model phân-loại-việc-được (claude-*) <= 1  → KHÔNG hỏi (route vô nghĩa).
//       • > 1                                         → HỎI enable; Yes → chạy picker.
//   - use-global: KHÔNG hỏi (copy raw global, không rõ provider/model thật).
//
// Tái dùng availableModels từ AI setup (đã fetch /v1/models 1 lần) — KHÔNG fetch lại.
// ai-orchestrator đã bị LỌC khỏi setupDefaultToolsOnInit (xử lý riêng ở đây).
//
// Fail-mềm: pack chưa có manifest ai-orchestrator → enableToolByName log warn,
// không crash init.

import type { AiSetupResult } from "./run-ai-setup-phase.js";
import { selectTierableModels } from "./setup-ai-orchestrator-model-tier-picker.js";
import { log } from "./terminal-logger.js";
import { enableToolByName } from "./tool-enable-disable-orchestrator.js";

const TOOL_NAME = "ai-orchestrator";

export async function maybePromptAiOrchestratorAfterAiSetup(
  workspacePath: string,
  aiResult: AiSetupResult | null,
  // forceRefresh: re-setup key (avatar ai setup) → ghi đè map cũ (model có thể đổi).
  // Mặc định false (init lần đầu — chưa có map nên không khác biệt).
  opts: { autoYes?: boolean; forceRefresh?: boolean } = {},
): Promise<void> {
  // AI setup bỏ qua / thất bại → không có model info → không hỏi.
  if (!aiResult || !aiResult.ok) return;

  // Đếm số model phân-loại-việc-được theo provider:
  //   - subscription: 3 alias cố định (opus/sonnet/haiku) — Claude Pro/Max/Team luôn
  //     có 3 model này → đủ phân loại việc → coi như 3 (picker tự đưa alias để map).
  //   - llmlite/anthropic: từ /v1/models đã fetch (ưu tiên claude-*).
  //   - use-global: không rõ provider/model thật (copy raw global) → KHÔNG hỏi.
  let tierableCount: number;
  if (aiResult.provider === "subscription") {
    tierableCount = 3;
  } else if (aiResult.provider === "llmlite" || aiResult.provider === "anthropic") {
    tierableCount = selectTierableModels(aiResult.availableModels ?? []).length;
  } else {
    return; // use-global / khác → không hỏi.
  }

  if (tierableCount <= 1) {
    // 1 model → route vô nghĩa → ẩn hoàn toàn (không hỏi, không ghi state, không merge).
    log.dim(
      `AI Orchestrator: key chỉ có ${tierableCount} model phân loại được — bỏ qua (cần >1 model).`,
    );
    return;
  }

  // >1 model → BẬT LUÔN (không hỏi 'có bật không' — user đã chốt tools là bắt buộc).
  // NHƯNG picker chọn model 3 tier vẫn interactive → non-TTY/autoYes không chạy được
  // → skip im, user bật tay sau bằng `avatar tools enable ai-orchestrator`.
  if (opts.autoYes) {
    log.dim(
      "AI Orchestrator: cần chọn model interactive — bỏ qua ở chế độ non-interactive. " +
        "Bật sau bằng 'avatar tools enable ai-orchestrator'.",
    );
    return;
  }

  log.info(
    "AI Orchestrator: key có >1 model → bật (chọn model cho mỗi loại việc). " +
      "Gỡ sau bằng 'avatar tools disable ai-orchestrator'.",
  );

  // enableToolByName → chạy setup-picker (hỏi 3 loại việc — BẮT BUỘC để có model
  // map + env fix) → merge manifest + set state. forceRefresh → ghi đè map cũ.
  await enableToolByName(workspacePath, TOOL_NAME, { forceRefresh: opts.forceRefresh });
}
